/** Geometry for the desktop bar. Coordinates are local to its display. */
export type StandaloneBarPlacementMode = "cursor" | "anchor" | "bottom";

export interface StandaloneBarPlacement {
  left: number;
  top: number;
  maxWidth: number;
  side: "right" | "left" | "fallback" | "anchor" | "bottom";
  originX: number;
  originY: number;
  growsUp: boolean;
}

/** The bar's margin from its viewport edges; a dragged bar stops here too. */
export const STANDALONE_BAR_INSET = 12;
const POINTER_GAP = 12;
const POINTER_CENTRE_OFFSET = 10;
const FALLBACK_GAP = 24;
export const STANDALONE_IDLE_WIDTH = 280;
export const STANDALONE_COLLAPSED_HEIGHT = 36;
export const STANDALONE_EXPANDED_HEIGHT = 68;

export function standaloneBarContainsGlobalPointer(
  bounds: { left: number; top: number; right: number; bottom: number } | null,
  pointer: { x: number; y: number; displayId: number | null },
  windowOrigin: { x: number; y: number },
  displayId: number | null,
): boolean {
  if (
    !bounds ||
    bounds.right <= bounds.left ||
    bounds.bottom <= bounds.top ||
    (displayId !== null && pointer.displayId !== displayId)
  )
    return false;
  const x = pointer.x - windowOrigin.x;
  const y = pointer.y - windowOrigin.y;
  return (
    x >= bounds.left &&
    x <= bounds.right &&
    y >= bounds.top &&
    y <= bounds.bottom
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

export function computeStandaloneBarPlacement({
  point,
  viewport,
  placement = "cursor",
  width = STANDALONE_IDLE_WIDTH,
  collapsedHeight = STANDALONE_COLLAPSED_HEIGHT,
  expandedHeight = collapsedHeight,
}: {
  point: { x: number; y: number };
  viewport: { width: number; height: number };
  placement?: StandaloneBarPlacementMode;
  width?: number;
  collapsedHeight?: number;
  expandedHeight?: number;
}): StandaloneBarPlacement {
  const { width: vw, height: vh } = viewport;
  const availableWidth = Math.max(0, vw - STANDALONE_BAR_INSET * 2);
  const idleWidth = Math.min(width, availableWidth);
  const maxLeft = Math.max(
    STANDALONE_BAR_INSET,
    vw - STANDALONE_BAR_INSET - idleWidth,
  );
  const maxTop = Math.max(
    STANDALONE_BAR_INSET,
    vh - STANDALONE_BAR_INSET - collapsedHeight,
  );

  if (placement === "bottom") {
    const left = clamp((vw - idleWidth) / 2, STANDALONE_BAR_INSET, maxLeft);
    const top = maxTop;
    return {
      left,
      top,
      maxWidth: availableWidth,
      side: "bottom",
      originX: idleWidth / 2,
      originY: collapsedHeight,
      growsUp: true,
    };
  }

  if (placement === "anchor") {
    const left = clamp(point.x, STANDALONE_BAR_INSET, maxLeft);
    const top = clamp(point.y, STANDALONE_BAR_INSET, maxTop);
    return {
      left,
      top,
      maxWidth: Math.max(0, vw - STANDALONE_BAR_INSET - left),
      side: "anchor",
      originX: 0,
      originY: collapsedHeight / 2,
      growsUp: top + expandedHeight > vh - STANDALONE_BAR_INSET,
    };
  }

  const anchorY = point.y + POINTER_CENTRE_OFFSET;
  if (point.x + POINTER_GAP + idleWidth <= vw - STANDALONE_BAR_INSET) {
    const left = point.x + POINTER_GAP;
    const top = clamp(
      anchorY - collapsedHeight / 2,
      STANDALONE_BAR_INSET,
      maxTop,
    );
    return {
      left,
      top,
      maxWidth: Math.max(0, vw - STANDALONE_BAR_INSET - left),
      side: "right",
      originX: 0,
      originY: clamp(anchorY - top, 3, collapsedHeight - 3),
      growsUp: top + expandedHeight > vh - STANDALONE_BAR_INSET,
    };
  }

  if (point.x - POINTER_GAP - idleWidth >= STANDALONE_BAR_INSET) {
    const right = point.x - POINTER_GAP;
    const top = clamp(
      anchorY - collapsedHeight / 2,
      STANDALONE_BAR_INSET,
      maxTop,
    );
    return {
      left: right - idleWidth,
      top,
      maxWidth: Math.max(0, right - STANDALONE_BAR_INSET),
      side: "left",
      originX: idleWidth,
      originY: clamp(anchorY - top, 3, collapsedHeight - 3),
      growsUp: top + expandedHeight > vh - STANDALONE_BAR_INSET,
    };
  }

  const left = clamp(point.x - POINTER_GAP, STANDALONE_BAR_INSET, maxLeft);
  const below = point.y + FALLBACK_GAP;
  const above = point.y - FALLBACK_GAP - collapsedHeight;
  const opensAbove = below + collapsedHeight > vh - STANDALONE_BAR_INSET;
  const top = clamp(opensAbove ? above : below, STANDALONE_BAR_INSET, maxTop);
  return {
    left,
    top,
    maxWidth: availableWidth,
    side: "fallback",
    originX: clamp(point.x - left, 3, idleWidth - 3),
    originY: opensAbove ? collapsedHeight : 0,
    growsUp: opensAbove || top + expandedHeight > vh - STANDALONE_BAR_INSET,
  };
}

/** How far an open bar has grown below its collapsed height. */
export function standaloneBarHeightGrowth(shellHeight: number): number {
  return Math.max(0, shellHeight - STANDALONE_COLLAPSED_HEIGHT);
}

/**
 * The "anchor" point that keeps a bar exactly where it was dropped. A bar
 * that grows up sits its growth above the placement's top, so the point
 * moves down by that growth.
 */
export function standaloneBarDropAnchor({
  corner,
  shellHeight,
  viewport,
}: {
  corner: { x: number; y: number };
  shellHeight: number;
  viewport: { width: number; height: number };
}): { x: number; y: number } {
  const { growsUp } = computeStandaloneBarPlacement({
    point: corner,
    viewport,
    placement: "anchor",
    expandedHeight: Math.max(shellHeight, STANDALONE_EXPANDED_HEIGHT),
  });
  return growsUp
    ? { x: corner.x, y: corner.y + standaloneBarHeightGrowth(shellHeight) }
    : corner;
}
